import React, { useState, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { PieChartFill } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { handleClaimDashboardFilter } from "../config/claimDashboardSlice";

const DEFAULT_STATUS_DISTRIBUTION_DATA = [
  {
    status: "Approved",
    count: 12450,
    amount: 3180200,
    grossAmount: 3540000,
    percentage: 67.1,
    color: "#059669",
  },
  {
    status: "Partially Approved",
    count: 1760,
    amount: 424700,
    grossAmount: 480000,
    percentage: 9.5,
    color: "#0D9488",
  },
  {
    status: "Rejected",
    count: 1420,
    amount: 510000,
    grossAmount: 510000,
    percentage: 7.7,
    color: "#E11D48",
  },
  {
    status: "Ready for Submission",
    count: 1132,
    amount: 285000,
    grossAmount: 310000,
    percentage: 6.1,
    color: "#2563EB",
  },
  {
    status: "Pending",
    count: 740,
    amount: 185000,
    grossAmount: 200000,
    percentage: 4.0,
    color: "#D97706",
  },
  {
    status: "Queued",
    count: 500,
    amount: 125000,
    grossAmount: 140000,
    percentage: 2.7,
    color: "#7C3AED",
  },
  {
    status: "Validation Error",
    count: 284,
    amount: 71000,
    grossAmount: 80000,
    percentage: 1.5,
    color: "#DC2626",
  },
  {
    status: "Invalid",
    count: 150,
    amount: 37500,
    grossAmount: 42000,
    percentage: 0.8,
    color: "#DB2777",
  },
  {
    status: "Cancelled",
    count: 106,
    amount: 26500,
    grossAmount: 30000,
    percentage: 0.6,
    color: "#64748B",
  },
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    return (
      <div
        className="p-3 rounded shadow-lg text-white"
        style={{
          backgroundColor: "#0F172A",
          border: "1px solid #334155",
          fontSize: "12px",
          minWidth: "220px",
        }}
      >
        <div
          className="d-flex align-items-center gap-2 mb-2 pb-1 border-bottom fw-bold"
          style={{ borderColor: "#334155" }}
        >
          <span
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              backgroundColor: item.color,
              display: "inline-block",
            }}
          ></span>
          <span>{item.status}</span>
        </div>
        <div className="d-flex justify-content-between gap-3 mb-1">
          <span style={{ color: "#94A3B8" }}>Claim Count:</span>
          <span className="fw-semibold">
            {Number(item.count).toLocaleString("en-US")} claims
          </span>
        </div>
        <div className="d-flex justify-content-between gap-3 mb-1">
          <span style={{ color: "#94A3B8" }}>Total Net SAR:</span>
          <span className="fw-bold" style={{ color: "#34D399" }}>
            SAR {Number(item.amount).toLocaleString("en-US")}
          </span>
        </div>
        <div className="d-flex justify-content-between gap-3 mb-1">
          <span style={{ color: "#94A3B8" }}>Gross Tariff SAR:</span>
          <span className="fw-semibold" style={{ color: "#CBD5E1" }}>
            SAR {Number(item.grossAmount).toLocaleString("en-US")}
          </span>
        </div>
        <div
          className="d-flex justify-content-between gap-3 pt-1 mt-1 border-top"
          style={{ borderColor: "#334155" }}
        >
          <span style={{ color: "#94A3B8" }}>Share of Total:</span>
          <span className="fw-bold" style={{ color: "#60A5FA" }}>
            {item.percentage}%
          </span>
        </div>
      </div>
    );
  }
  return null;
};

const StatusDonutChart = ({
  data = DEFAULT_STATUS_DISTRIBUTION_DATA,
  selectedStatus,
  onSelectStatus,
}) => {
  const dispatch = useDispatch();
  const reduxActiveStatus = useSelector(
    (state) => state?.claimDashboard?.claimDashboardFilters?.status || "All",
  );
  const [internalStatus, setInternalStatus] = useState("All");

  const currentStatus =
    selectedStatus !== undefined && selectedStatus !== "All"
      ? selectedStatus
      : reduxActiveStatus !== "All"
        ? reduxActiveStatus
        : internalStatus;

  const totalCount = useMemo(
    () => data.reduce((acc, curr) => acc + (Number(curr.count) || 0), 0),
    [data],
  );
  const approvedItem = useMemo(
    () => data.find((d) => d.status === "Approved"),
    [data],
  );
  const approvedPercent = approvedItem ? approvedItem.percentage : 67.1;

  const handleStatusSelect = (statusName) => {
    const nextStatus = currentStatus === statusName ? "All" : statusName;
    setInternalStatus(nextStatus);
    if (onSelectStatus) {
      onSelectStatus(nextStatus);
    }
    dispatch(
      handleClaimDashboardFilter({
        check: "status",
        value: nextStatus === "All" ? null : nextStatus,
      }),
    );
  };

  return (
    <Col xs={12} className="mt-3">
      <Card className="shadow-sm border h-100">
        {/* Header */}
        <Card.Header className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between p-3 border-bottom gap-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "8px",
                backgroundColor: "rgba(59, 130, 246, 0.12)",
                color: "#3B82F6",
                border: "1px solid rgba(59, 130, 246, 0.25)",
              }}
            >
              <PieChartFill size={15} />
            </div>
            <div>
              <h6
                className="mb-0 fw-bold"
                style={{ fontSize: "15px" }}
              >
                Claims Status Distribution
              </h6>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                Breakdown of all {totalCount.toLocaleString("en-US")} active &
                historical claim statuses
              </div>
            </div>
          </div>

          {currentStatus !== "All" && (
            <button
              onClick={() => handleStatusSelect("All")}
              className="btn btn-sm btn-link text-decoration-none fw-semibold p-0"
              style={{ fontSize: "12px", color: "#3B82F6" }}
            >
              Clear Filter ({currentStatus})
            </button>
          )}
        </Card.Header>

        <Card.Body className="p-4">
          <Row className="g-4 align-items-center">
            {/* Left Column: Donut Chart with Center Stats */}
            <Col xs={12} lg={4}>
              <div
                className="position-relative d-flex justify-content-center align-items-center"
                style={{ height: "260px" }}
              >
                {/* Center overlay label - placed first in DOM order so chart and tooltip render above it */}
                <div
                  className="position-absolute top-50 start-50 translate-middle d-flex flex-column align-items-center justify-content-center pointer-events-none text-center"
                  style={{ pointerEvents: "none", zIndex: 0 }}
                >
                  <span
                    className="text-uppercase fw-semibold text-muted"
                    style={{
                      fontSize: "10px",
                      letterSpacing: "0.5px",
                    }}
                  >
                    TOTAL CLAIMS
                  </span>
                  <span
                    className="fw-bold my-1"
                    style={{ fontSize: "22px", lineHeight: "1" }}
                  >
                    {totalCount.toLocaleString("en-US")}
                  </span>
                  <span
                    className="fw-semibold px-2 py-1 rounded-pill"
                    style={{
                      fontSize: "11px",
                      color: "#10B981",
                      backgroundColor: "rgba(16, 185, 129, 0.15)",
                    }}
                  >
                    {approvedPercent}% Approved
                  </span>
                </div>

                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    position: "relative",
                    zIndex: 10,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                      <Pie
                        data={data}
                        cx="50%"
                        cy="50%"
                        innerRadius={70}
                        outerRadius={100}
                        paddingAngle={2.5}
                        dataKey="count"
                        onClick={(entry) =>
                          handleStatusSelect(entry?.status || "All")
                        }
                        style={{ cursor: "pointer" }}
                      >
                        {data.map((entry, index) => {
                          const isSelected =
                            currentStatus === "All" ||
                            currentStatus === entry.status;
                          return (
                            <Cell
                              key={`cell-${index}`}
                              fill={entry.color}
                              stroke={
                                currentStatus === entry.status
                                  ? "currentColor"
                                  : "transparent"
                              }
                              strokeWidth={
                                currentStatus === entry.status ? 3 : 0
                              }
                              opacity={isSelected ? 1 : 0.35}
                            />
                          );
                        })}
                      </Pie>
                      <Tooltip
                        content={<CustomTooltip />}
                        wrapperStyle={{ zIndex: 1000 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Col>

            {/* Right Column: Interactive Legend Grid */}
            <Col xs={12} lg={8}>
              <div className="d-flex flex-wrap gap-2 justify-content-between">
                {data.map((item) => {
                  const isSelected = currentStatus === item.status;
                  return (
                    <button
                      key={item.status}
                      onClick={() => handleStatusSelect(item.status)}
                      className="btn d-flex align-items-center justify-content-between p-2 rounded text-start transition-all"
                      style={{
                        flex: "1 1 30%",
                        minWidth: "180px",
                        fontSize: "12px",
                        backgroundColor: isSelected
                          ? "rgba(100, 116, 139, 0.15)"
                          : "transparent",
                        border: isSelected
                          ? "1px solid rgba(100, 116, 139, 0.5)"
                          : "1px solid rgba(100, 116, 139, 0.2)",
                        fontWeight: isSelected ? 700 : 500,
                      }}
                    >
                      <div
                        className="d-flex align-items-center gap-2 text-truncate"
                        style={{ maxWidth: "75%" }}
                      >
                        <span
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            backgroundColor: item.color,
                            flexShrink: 0,
                            display: "inline-block",
                          }}
                        ></span>
                        <span className="text-truncate">
                          {item.status}
                        </span>
                      </div>
                      <span className="fw-bold ms-2">
                        {Number(item.count).toLocaleString("en-US")}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default StatusDonutChart;
